"use client";

import { useCallback, useEffect, useState } from "react";
import { fetchWithAuth } from "@/lib/auth";

/**
 * Add, list and remove temporary helpers on one event.
 *
 * Shown to staff and to the owning group's chairs/vice-chairs (the backend sends
 * `can_manage_event_helpers` on the meeting detail). Adding is only possible up
 * until the event concludes; after that the list stays visible and revocable,
 * because existing helpers keep working through the one-week tail.
 */

interface Helper {
    id: string;
    user_id: string;
    user_name?: string;
    user_email?: string;
    capabilities: string[];
    status: string;
    note?: string;
    granted_by_name?: string;
    access_until?: string;
    is_active: boolean;
    revoked_by_staff: boolean;
}

/** A person who could be added — from staff search or the chair email lookup. */
interface Candidate {
    user_id: string;
    name: string;
    email: string;
    job_title?: string;
    company?: string;
    profile_photo_url?: string;
    already_has_access: boolean;
    already_helper: boolean;
    blocked_reason?: string;
}

const CAPABILITY_LABELS: Record<string, string> = {
    view_event: "View event",
    pre_event_checklist: "Checklist",
    live_log: "Session details",
    media_upload: "Photos",
};

function formatWhen(iso?: string): string {
    if (!iso) return "—";
    return new Date(iso).toLocaleString(undefined, {
        day: "numeric", month: "short", hour: "numeric", minute: "2-digit",
    });
}

export default function EventHelpersPanel({
    meetingId,
    isStaff = false,
}: {
    meetingId: string;
    /** Staff get directory search by name; chairs get exact-email lookup only. */
    isStaff?: boolean;
}) {
    const [helpers, setHelpers] = useState<Helper[]>([]);
    const [canAdd, setCanAdd] = useState(true);
    const [blockedReason, setBlockedReason] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [showAdd, setShowAdd] = useState(false);

    const load = useCallback(async () => {
        try {
            const res = await fetchWithAuth(`/api/v1/meetings/${meetingId}/helpers`);
            if (!res.ok) return;
            const data = await res.json();
            setHelpers(data.helpers || []);
            setCanAdd(!!data.can_add);
            setBlockedReason(data.add_blocked_reason || null);
        } finally {
            setLoading(false);
        }
    }, [meetingId]);

    useEffect(() => { load(); }, [load]);

    const revoke = async (helper: Helper) => {
        const reason = window.prompt(
            `Remove ${helper.user_name || "this helper"}'s access to this event?\n\nOptional reason:`,
        );
        if (reason === null) return;
        const res = await fetchWithAuth(`/api/v1/meetings/${meetingId}/helpers/${helper.id}`, {
            method: "DELETE",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ reason: reason || null }),
        });
        if (res.ok) load();
        else alert((await res.json().catch(() => ({}))).detail || "Could not remove that helper.");
    };

    const active = helpers.filter((h) => h.is_active);
    const past = helpers.filter((h) => !h.is_active);

    return (
        <section className="bg-white rounded-xl border border-[#e2e8f0] p-5">
            <div className="flex items-start justify-between gap-4 mb-1">
                <div>
                    <h3 className="text-sm font-black text-[#003d45] uppercase tracking-tight">
                        Temporary Volunteers
                    </h3>
                    <p className="text-[11px] text-[#64748b] mt-0.5">
                        People helping at this event who aren&apos;t in the group. They can add
                        photos, session details and work the checklist. Access ends automatically
                        one week after the event.
                    </p>
                </div>
                <button
                    onClick={() => setShowAdd(true)}
                    disabled={!canAdd}
                    title={!canAdd ? blockedReason || undefined : undefined}
                    className="flex-shrink-0 px-3 py-2 rounded-lg text-[11px] font-black uppercase tracking-wide
                               bg-[#007a8a] text-white hover:bg-[#006673] transition-colors
                               disabled:bg-[#e2e8f0] disabled:text-[#94a3b8] disabled:cursor-not-allowed"
                >
                    + Add volunteer
                </button>
            </div>

            {/* Say WHY the button is off rather than hiding it — a chair looking
                for the button needs to know the window closed, not wonder. */}
            {!canAdd && blockedReason && (
                <p className="text-[11px] text-[#92400e] bg-[#fff8e6] border border-[#f0d9a0]
                              rounded-lg px-3 py-2 mt-3">
                    {blockedReason}
                </p>
            )}

            <div className="mt-4 space-y-2">
                {loading && <p className="text-[11px] text-[#94a3b8]">Loading…</p>}

                {!loading && active.length === 0 && (
                    <p className="text-[11px] text-[#94a3b8] py-2">No helpers on this event yet.</p>
                )}

                {active.map((h) => (
                    <div key={h.id}
                         className="flex items-start justify-between gap-3 p-3 rounded-lg bg-[#f8fafc]
                                    border border-[#e2e8f0]">
                        <div className="min-w-0">
                            <p className="text-xs font-black text-[#003d45] truncate">{h.user_name}</p>
                            <p className="text-[10px] text-[#64748b] truncate">{h.user_email}</p>
                            {h.note && <p className="text-[10px] text-[#64748b] italic mt-1">{h.note}</p>}
                            <div className="flex flex-wrap gap-1 mt-2">
                                {h.capabilities.map((c) => (
                                    <span key={c}
                                          className="px-1.5 py-0.5 rounded bg-[#007a8a]/10 text-[#007a8a]
                                                     text-[9px] font-bold uppercase tracking-wide">
                                        {CAPABILITY_LABELS[c] || c}
                                    </span>
                                ))}
                            </div>
                            <p className="text-[10px] text-[#94a3b8] mt-2">
                                Until {formatWhen(h.access_until)}
                                {h.granted_by_name ? ` · added by ${h.granted_by_name}` : ""}
                            </p>
                        </div>
                        <button
                            onClick={() => revoke(h)}
                            className="flex-shrink-0 text-[10px] font-black uppercase tracking-wide
                                       text-[#ae232d] hover:underline"
                        >
                            Remove
                        </button>
                    </div>
                ))}

                {past.length > 0 && (
                    <details className="mt-3">
                        <summary className="text-[10px] font-bold text-[#94a3b8] uppercase tracking-wide cursor-pointer">
                            Past helpers ({past.length})
                        </summary>
                        <div className="mt-2 space-y-1">
                            {past.map((h) => (
                                <p key={h.id} className="text-[10px] text-[#94a3b8]">
                                    {h.user_name} — {h.status}
                                    {h.revoked_by_staff ? " by CMT staff" : ""}
                                </p>
                            ))}
                        </div>
                    </details>
                )}
            </div>

            {showAdd && (
                <AddHelperModal
                    meetingId={meetingId}
                    isStaff={isStaff}
                    onClose={() => setShowAdd(false)}
                    onAdded={() => { setShowAdd(false); load(); }}
                />
            )}
        </section>
    );
}

/**
 * Add an existing platform user as a temporary helper.
 *
 * Two ways in, on purpose:
 *  • Staff search the directory by name. They already hold an equivalent search
 *    (`/organizations/users/search`), so this grants them nothing new.
 *  • A group chair enters the person's full email address. Giving every chapter
 *    chair a name-browsable index of the whole platform is not a trade worth
 *    making for the convenience — and whoever is adding a helper already knows
 *    who they are asking for.
 *
 * Either way the person must already have an account. They sign in the normal
 * way (SSO); nothing here creates a login.
 */
function AddHelperModal({
    meetingId, isStaff, onClose, onAdded,
}: { meetingId: string; isStaff: boolean; onClose: () => void; onAdded: () => void }) {
    const [query, setQuery] = useState("");
    const [note, setNote] = useState("");
    const [results, setResults] = useState<Candidate[] | null>(null);
    const [picked, setPicked] = useState<Candidate | null>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const find = async () => {
        const term = query.trim();
        if (!term) return;
        setBusy(true); setError(null); setResults(null); setPicked(null);
        try {
            const url = isStaff
                ? `/api/v1/meetings/${meetingId}/helpers/search?q=${encodeURIComponent(term)}`
                : `/api/v1/meetings/${meetingId}/helpers/lookup?email=${encodeURIComponent(term)}`;
            const res = await fetchWithAuth(url);
            if (!res.ok) {
                setError((await res.json().catch(() => ({}))).detail || "Search failed.");
                return;
            }
            const data = await res.json();
            if (isStaff) {
                setResults(data as Candidate[]);
            } else {
                // The lookup endpoint returns a single record, not a list.
                setResults(data.found ? [{
                    user_id: data.user_id,
                    name: data.name,
                    email: term,
                    job_title: data.job_title,
                    company: data.company,
                    already_helper: data.already_helper,
                    already_has_access: data.already_has_access,
                    blocked_reason: data.blocked_reason,
                }] : []);
            }
        } finally {
            setBusy(false);
        }
    };

    const add = async () => {
        if (!picked) return;
        setBusy(true); setError(null);
        try {
            const res = await fetchWithAuth(`/api/v1/meetings/${meetingId}/helpers`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ user_id: picked.user_id, note: note.trim() || null }),
            });
            if (!res.ok) {
                setError((await res.json().catch(() => ({}))).detail || "Could not add that helper.");
                return;
            }
            onAdded();
        } finally {
            setBusy(false);
        }
    };

    const blockedReason = (c: Candidate) =>
        c.blocked_reason
        || (c.already_has_access ? "Already a member of this group." : null)
        || (c.already_helper ? "Already helping at this event." : null);

    return (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
             onClick={onClose}>
            <div className="bg-white rounded-xl p-5 w-full max-w-md max-h-[85vh] overflow-y-auto"
                 onClick={(e) => e.stopPropagation()}>
                <h4 className="text-sm font-black text-[#003d45] uppercase tracking-tight">
                    Add a temporary volunteer
                </h4>
                <p className="text-[11px] text-[#64748b] mt-1 mb-4">
                    {isStaff
                        ? "Search by name or email. They must already have an account — they sign in the usual way."
                        : "Enter the person's full email address. They must already have an account."}
                </p>

                <label className="block text-[10px] font-black uppercase tracking-wide text-[#64748b] mb-1">
                    {isStaff ? "Name or email" : "Email address"}
                </label>
                <div className="flex gap-2">
                    <input
                        type={isStaff ? "text" : "email"}
                        value={query}
                        onChange={(e) => { setQuery(e.target.value); setResults(null); setPicked(null); }}
                        onKeyDown={(e) => { if (e.key === "Enter") find(); }}
                        placeholder={isStaff ? "Jane Doe" : "person@example.com"}
                        className="flex-1 px-3 py-2 rounded-lg border border-[#e2e8f0] text-xs
                                   focus:outline-none focus:border-[#007a8a]"
                    />
                    <button onClick={find} disabled={busy || !query.trim()}
                            className="px-3 py-2 rounded-lg bg-[#003d45] text-white text-[11px] font-black
                                       uppercase tracking-wide disabled:bg-[#e2e8f0] disabled:text-[#94a3b8]">
                        {isStaff ? "Search" : "Find"}
                    </button>
                </div>

                {results?.length === 0 && (
                    <p className="text-[11px] text-[#ae232d] mt-3">
                        No account found. They need to be on the platform before they can help.
                    </p>
                )}

                {!!results?.length && (
                    <div className="mt-3 space-y-1.5">
                        {results.map((c) => {
                            const blocked = blockedReason(c);
                            const selected = picked?.user_id === c.user_id;
                            return (
                                <button
                                    key={c.user_id}
                                    disabled={!!blocked}
                                    onClick={() => setPicked(c)}
                                    className={`w-full text-left p-3 rounded-lg border transition-colors
                                        ${selected ? "border-[#007a8a] bg-[#007a8a]/5"
                                                   : "border-[#e2e8f0] hover:border-[#cbd5e1]"}
                                        ${blocked ? "opacity-50 cursor-not-allowed" : ""}`}
                                >
                                    <p className="text-xs font-black text-[#003d45]">{c.name}</p>
                                    <p className="text-[10px] text-[#64748b] truncate">{c.email}</p>
                                    {(c.job_title || c.company) && (
                                        <p className="text-[10px] text-[#64748b]">
                                            {[c.job_title, c.company].filter(Boolean).join(" · ")}
                                        </p>
                                    )}
                                    {blocked && (
                                        <p className="text-[10px] text-[#92400e] mt-1">{blocked}</p>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                )}

                {picked && !blockedReason(picked) && (
                    <>
                        <label className="block text-[10px] font-black uppercase tracking-wide
                                          text-[#64748b] mt-4 mb-1">
                            What are they helping with? (optional)
                        </label>
                        <input
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="AV volunteer from the venue"
                            className="w-full px-3 py-2 rounded-lg border border-[#e2e8f0] text-xs
                                       focus:outline-none focus:border-[#007a8a]"
                        />
                        <p className="text-[10px] text-[#64748b] mt-2">
                            They&apos;ll be able to add photos, session details and work the
                            pre-event checklist. Access ends a week after the event, automatically.
                        </p>
                    </>
                )}

                {error && <p className="text-[11px] text-[#ae232d] mt-3">{error}</p>}

                <div className="flex justify-end gap-2 mt-5">
                    <button onClick={onClose}
                            className="px-3 py-2 text-[11px] font-black uppercase tracking-wide text-[#64748b]">
                        Cancel
                    </button>
                    <button
                        onClick={add}
                        disabled={busy || !picked || !!blockedReason(picked)}
                        className="px-4 py-2 rounded-lg bg-[#007a8a] text-white text-[11px] font-black
                                   uppercase tracking-wide disabled:bg-[#e2e8f0] disabled:text-[#94a3b8]"
                    >
                        Give access
                    </button>
                </div>
            </div>
        </div>
    );
}
